/**
 * Notification Redux Slice
 * 
 * Manages notification state including:
 * - Notification list
 * - Unread count
 * - Loading states
 * - Real-time updates
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Notification } from '@/app/features/notifications/types/notification.types';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAsRead,
  dismissNotification,
  markAllAsRead,
  clearAllRead,
  markNotificationsAsRead,
  deleteNotification,
  deleteNotifications,
} from '../thunks/notificationThunks';

/**
 * Notification state interface
 */
export interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;
  lastFetchTime: number | null;
  filter: {
    isRead?: boolean;
    priority?: string;
    category?: string;
  };
}

/**
 * Initial state
 */
const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  isLoading: false,
  error: null,
  lastFetchTime: null,
  filter: {},
};

/**
 * Notification slice
 */
const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    /**
     * Add new notification (for real-time updates)
     */
    addNotification: (state, action: PayloadAction<Notification>) => {
      // Add to beginning of list
      state.notifications.unshift(action.payload);
      
      // Increment unread count if notification is unread
      if (!action.payload.isRead) {
        state.unreadCount += 1;
      }
      
      // Increment total count
      state.totalCount += 1;
      
      // Remove oldest notification if exceeding page size
      if (state.notifications.length > state.pageSize) {
        state.notifications.pop();
      }
    },

    /**
     * Update notification read status (for real-time updates)
     */
    updateNotificationReadStatus: (
      state,
      action: PayloadAction<{ notificationId: number; readAt: string }>
    ) => {
      const notification = state.notifications.find(n => n.id === action.payload.notificationId);
      if (notification && !notification.isRead) {
        notification.isRead = true;
        notification.readAt = action.payload.readAt;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },

    /**
     * Remove notification (for real-time updates)
     */
    removeNotification: (state, action: PayloadAction<number>) => {
      const index = state.notifications.findIndex(n => n.id === action.payload);
      if (index !== -1) {
        const notification = state.notifications[index];
        if (!notification.isRead) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.notifications.splice(index, 1);
        state.totalCount = Math.max(0, state.totalCount - 1);
      }
    },

    /**
     * Mark all notifications as read locally (for real-time updates)
     */
    markAllAsReadLocal: (state) => {
      state.notifications.forEach(notification => {
        if (!notification.isRead) {
          notification.isRead = true;
          notification.readAt = new Date().toISOString();
        }
      });
      state.unreadCount = 0;
    },

    /**
     * Update unread count (for real-time updates)
     */
    updateUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },

    /**
     * Set filter
     */
    setFilter: (
      state,
      action: PayloadAction<{ isRead?: boolean; priority?: string; category?: string }>
    ) => {
      state.filter = action.payload;
    },

    /**
     * Clear filter
     */
    clearFilter: (state) => {
      state.filter = {};
    },

    /**
     * Reset notifications state
     */
    resetNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      state.totalCount = 0;
      state.currentPage = 1;
      state.isLoading = false;
      state.error = null;
      state.lastFetchTime = null;
      state.filter = {};
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch notifications
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications = action.payload.notifications;
        state.totalCount = action.payload.totalCount;
        state.currentPage = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.lastFetchTime = Date.now();
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch unread count
    builder
      .addCase(fetchUnreadCount.pending, (_state) => {
        // Don't set loading for unread count to avoid UI flicker
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      .addCase(fetchUnreadCount.rejected, (_state, action) => {
        // Silently fail for unread count
        console.error('Failed to fetch unread count:', action.payload);
      });

    // Mark as read
    builder
      .addCase(markAsRead.pending, (_state) => {
        // Optimistically update UI
      })
      .addCase(markAsRead.fulfilled, (state, action) => {
        const notification = state.notifications.find(n => n.id === action.payload);
        if (notification && !notification.isRead) {
          notification.isRead = true;
          notification.readAt = new Date().toISOString();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAsRead.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Dismiss notification
    builder
      .addCase(dismissNotification.pending, (_state) => {
        // Optimistically update UI
      })
      .addCase(dismissNotification.fulfilled, (state, action) => {
        const index = state.notifications.findIndex(n => n.id === action.payload);
        if (index !== -1) {
          const notification = state.notifications[index];
          if (!notification.isRead) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
          state.notifications.splice(index, 1);
          state.totalCount = Math.max(0, state.totalCount - 1);
        }
      })
      .addCase(dismissNotification.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Mark all as read
    builder
      .addCase(markAllAsRead.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.isLoading = false;
        state.notifications.forEach(notification => {
          if (!notification.isRead) {
            notification.isRead = true;
            notification.readAt = new Date().toISOString();
          }
        });
        state.unreadCount = 0;
      })
      .addCase(markAllAsRead.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Clear all read
    builder
      .addCase(clearAllRead.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(clearAllRead.fulfilled, (state) => {
        state.isLoading = false;
        // Remove all read notifications
        state.notifications = state.notifications.filter(n => !n.isRead);
        state.totalCount = state.notifications.length;
      })
      .addCase(clearAllRead.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Mark multiple notifications as read
    builder
      .addCase(markNotificationsAsRead.pending, (_state) => {
        // Optimistically update UI
      })
      .addCase(markNotificationsAsRead.fulfilled, (state, action) => {
        const notificationIds = action.payload;
        notificationIds.forEach(id => {
          const notification = state.notifications.find(n => n.id === id);
          if (notification && !notification.isRead) {
            notification.isRead = true;
            notification.readAt = new Date().toISOString();
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        });
      })
      .addCase(markNotificationsAsRead.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete single notification
    builder
      .addCase(deleteNotification.pending, (_state) => {
        // Optimistically update UI
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const index = state.notifications.findIndex(n => n.id === action.payload);
        if (index !== -1) {
          const notification = state.notifications[index];
          if (!notification.isRead) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
          state.notifications.splice(index, 1);
          state.totalCount = Math.max(0, state.totalCount - 1);
        }
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete multiple notifications
    builder
      .addCase(deleteNotifications.pending, (_state) => {
        // Optimistically update UI
      })
      .addCase(deleteNotifications.fulfilled, (state, action) => {
        const notificationIds = action.payload;
        notificationIds.forEach(id => {
          const index = state.notifications.findIndex(n => n.id === id);
          if (index !== -1) {
            const notification = state.notifications[index];
            if (!notification.isRead) {
              state.unreadCount = Math.max(0, state.unreadCount - 1);
            }
            state.notifications.splice(index, 1);
            state.totalCount = Math.max(0, state.totalCount - 1);
          }
        });
      })
      .addCase(deleteNotifications.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Reset notifications on account switch
    builder.addMatcher(
      (action) => action.type === 'auth/switchAccount/fulfilled',
      (state) => {
        console.log('[notificationSlice] Account switched, resetting notifications');
        // Reset all notification state for the new account
        state.notifications = [];
        state.unreadCount = 0;
        state.totalCount = 0;
        state.currentPage = 1;
        state.isLoading = false;
        state.error = null;
        state.lastFetchTime = null;
        state.filter = {};
      }
    );
  },
});

export const {
  addNotification,
  updateNotificationReadStatus,
  removeNotification,
  markAllAsReadLocal,
  updateUnreadCount,
  setFilter,
  clearFilter,
  resetNotifications,
  clearError,
} = notificationSlice.actions;

export default notificationSlice.reducer;
